// =============================================================================
// Image upload — combines client-side processing + presign + PUT + record
// =============================================================================
// Encapsulates the full pipeline so UI components don't have to care about
// the multi-step dance:
//   1. Process the file (resize, re-encode as WebP)
//   2. Ask the backend for a presigned upload URL
//   3. PUT the processed blob directly to MinIO
//   4. Tell the backend to record the new key on the user/server row
// =============================================================================

import { api } from './api';
import { processImageForAvatar, processImageForBanner } from './utils/imageProcessor';

export async function uploadAvatar(file: File): Promise<string | null> {
  const processed = await processImageForAvatar(file);

  const presign = await api.presignImage({
    kind: 'avatar',
    contentType: processed.contentType,
    sizeBytes: processed.sizeBytes,
  });

  // PUT directly to MinIO with the presigned URL
  const putResp = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': processed.contentType },
    body: processed.blob,
  });
  if (!putResp.ok) {
    throw new Error(`Upload failed: ${putResp.status} ${putResp.statusText}`);
  }

  const result = await api.setMyAvatar(presign.storageKey);
  return result.avatarUrl;
}

export async function uploadProfileBanner(file: File): Promise<string | null> {
  const processed = await processImageForBanner(file);

  const presign = await api.presignImage({
    kind: 'profile-banner',
    contentType: processed.contentType,
    sizeBytes: processed.sizeBytes,
  });

  const putResp = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': processed.contentType },
    body: processed.blob,
  });
  if (!putResp.ok) {
    throw new Error(`Upload failed: ${putResp.status} ${putResp.statusText}`);
  }

  const result = await api.setMyBanner(presign.storageKey);
  return result.bannerUrl;
}

export async function uploadProfileBackground(file: File): Promise<string | null> {
  const processed = await processImageForBanner(file, { maxDimension: 1600, targetBytes: 600 * 1024 });

  const presign = await api.presignImage({
    kind: 'profile-background',
    contentType: processed.contentType,
    sizeBytes: processed.sizeBytes,
  });

  const putResp = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': processed.contentType },
    body: processed.blob,
  });
  if (!putResp.ok) {
    throw new Error(`Upload failed: ${putResp.status} ${putResp.statusText}`);
  }

  const result = await api.setMyBackground(presign.storageKey);
  return result.backgroundUrl;
}

export async function uploadServerIcon(serverId: string, file: File): Promise<string | null> {
  const processed = await processImageForAvatar(file);

  const presign = await api.presignImage({
    kind: 'icon',
    serverId,
    contentType: processed.contentType,
    sizeBytes: processed.sizeBytes,
  });

  const putResp = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': processed.contentType },
    body: processed.blob,
  });
  if (!putResp.ok) {
    throw new Error(`Upload failed: ${putResp.status} ${putResp.statusText}`);
  }

  const result = await api.setServerIcon(serverId, presign.storageKey);
  return result.iconUrl;
}
